import z from 'zod';
import { MODULE_EXCHANGE_GROUPS } from './module-exchange-events-ready.webhook.js';
/**
 * Most rows one envelope carries (DR-5574). Brain keeps the first rows in its own order, drops the rest
 * and counts them in `rowsOmitted`.
 */
export const ENGINE_CONFIGURATION_ROWS_CAP = 20_000;
/** Longest value any string field of a row carries (DR-5574). Brain truncates a longer value to this cap. */
export const ENGINE_CONFIGURATION_ROW_STRING_CAP = 500;
const capped = () => z.string().max(ENGINE_CONFIGURATION_ROW_STRING_CAP);
/**
 * A project's engine configuration rows (DR-5574): every part row of every engine configuration
 * print (`Engine Configuration List` page) — shop parts-replaced lists and operator configuration
 * lists — life-limited parts, accessories and engine-chapter rows alike.
 *
 * Rinoj's rule (2026-10-22): on a shop print with IN / OUT columns (ST Aerospace PART INPUT / PART
 * OUTPUT, Delta INCOMING / OUTGOING, GE OFF LOG / ON LOG, StandardAero Removed / Installed, GE Wales
 * stacked IN / OUT sub-rows), the outgoing / installed / on / OUT side IS the configuration at
 * release: it fills `partNumber` / `serialNumber` / `quantity`. The incoming / removed / off / IN side
 * is what came into the shop: it fills `removedPartNumber` / `removedSerialNumber` /
 * `removedQuantity`. An operator list (AMOS "Component Equipment List Report") shows one state,
 * valid on its print date: every row is the installed identity, with no removed side. A
 * removal-only print fills only the removed side, so a row carries `partNumber` or
 * `removedPartNumber`, not always both. `workOrder` and `visitNumber` are separate fields.
 *
 * A row ships when it has a part number OR a serial number on either side (DR-5695): a row that
 * prints a serial but no part number is still a part on the print.
 *
 * Counters (DR-5695, DR-5754): `tsn` / `csn` / `tso` / `cso` are the INSTALLED side's hours and
 * cycles. `removedTsn` / `removedCsn` / `removedTso` / `removedCso` are the REMOVED side's hours
 * and cycles. Both sets are read from the row's own columns. They are never the engine header
 * TSN / CSN. `tso` / `cso` and `removedTso` / `removedCso` come only from a since-overhaul column
 * (never since shop visit or TSHM / CSHM). `sourceEsn` is the donor engine serial the installed
 * part came from (a FROM / SOURCE ENGINE cell or a "from ESN" remark), verbatim. Each field is
 * absent when the print does not state it. Brain never sends null.
 *
 * Module group (DR-5776): `moduleGroup` is the sub-module group of the row's unit, by the same
 * mapping the module exchange detection uses (`module-exchange-events-ready`): a CFM56 module row
 * (a module serial such as `31X94113`, or MODULE in the description) whose serial digits or
 * description name a group. Brain reads the installed serial, else the removed serial (an off-log
 * row). The field is absent when the row is not a module row, the group is unknown, or the two
 * sides name different groups. It does not depend on the print: a parts list row with a module
 * serial carries it too, so a consumer filters on `printTitle` when it wants module logs only. A
 * row that prints no `engineModel` passes the CFM56 gate only on the module serial shape. Never
 * null.
 *
 * Emitted as ONE webhook per project carrying a flat array. Each row keeps the `documentId` of its
 * source document, the `pageId` it was extracted from and its `reconstructedDocumentId`;
 * `(pageId, rowIndex)` names the printed row and is unique within an envelope (the schema rejects a
 * repeat): a consumer keys rows on it, since a row such as a blade batch has no serial. The same
 * part can also arrive in the `llp` and `qec` families, which read the same pages for their own
 * purpose: that duplication is intended.
 *
 * Versioning: `generation` is the persisted finalize counter (DR-3342) and `generatedAt` the
 * persisted finalize timestamp, so a retry of one finalize is byte-identical. Brain always sets
 * both; `generation` stays optional in this schema only.
 *
 * Partial pushes: the body travels as one Pub/Sub message (limit 10,000,000 bytes). The stored
 * project data keeps every row up to `ENGINE_CONFIGURATION_ROWS_CAP`. A push whose rows would not
 * fit Brain's byte ceiling ships the longest prefix of the stored rows that fits, in order.
 * `rowsOmitted` counts every row the push leaves out (over the row cap, plus over the byte ceiling);
 * it is absent, never 0, when the push is complete. A push with `rowsOmitted` is partial. The
 * consumer pulls the rest from `GET /api/projects/:id/engine-configuration-rows` (DR-5633) with the
 * push's `generation`, from `offset` = the push's `engineConfigurationRows.length` (the raw
 * envelope's array length), and follows `nextOffset` to the end. A 409 means a newer snapshot
 * exists: wait for its push, and never mix two snapshots. The route's `rowsOmitted` counts only the
 * row-cap omissions, which no route serves, so a push with `rowsOmitted` can already hold every
 * stored row (when only the row cap cut). The largest stored set on dev (2026-10-24) is 5,364 rows,
 * about 3.2 MB.
 *
 * `esn` is the project's working engine — informational only. Rows are NOT filtered to it; each
 * row carries its own print's `esn`.
 *
 * Retraction (BP-21): an empty `engineConfigurationRows` array retracts the project's stored rows
 * (a project whose configuration prints are gone, or whose report has no configuration section).
 * Brain never emits `null` for the array.
 *
 * Rollout: Brain sends this family only while `ENGINE_CONFIGURATION_ROWS_READY_ENABLED=true` (off
 * by default), because Oil rejects an unknown `type`.
 */
export const engineConfigurationRowsReadyWebhookSchema = z.looseObject({
    type: z.literal('engine-configuration-rows-ready'),
    projectId: z.string(),
    // Required, unlike the older families: the family is new and Brain always emits it.
    generatedAt: z.iso.datetime(),
    // Brain always emits it (DR-3342); optional in the schema at the consumer's request.
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    esn: z.string().nullable(),
    // Rows the push leaves out (row cap plus byte ceiling); absent when the push is complete. The
    // byte-ceiling rows are on `GET /api/projects/:id/engine-configuration-rows` (DR-5633).
    rowsOmitted: z.number().int().positive().optional(),
    engineConfigurationRows: z
        .array(z.looseObject({
        // 1-based printed row order on the page.
        rowIndex: z.number().int().positive(),
        itemNumber: capped().optional(),
        description: capped(),
        // Installed (outgoing / on / OUT) side.
        partNumber: capped().optional(),
        serialNumber: capped().optional(),
        quantity: z.number().int().positive().optional(),
        position: capped().optional(),
        // Removed (incoming / off / IN) side.
        removedPartNumber: capped().optional(),
        removedSerialNumber: capped().optional(),
        removedQuantity: z.number().int().positive().optional(),
        // Installed side's counters (DR-5695): the row's own columns, never the engine header.
        tsn: z.number().nonnegative().optional(),
        csn: z.number().nonnegative().optional(),
        // Since-overhaul columns only, never since shop visit.
        tso: z.number().nonnegative().optional(),
        cso: z.number().nonnegative().optional(),
        // The engine the installed part came from, verbatim (DR-5695).
        sourceEsn: capped().optional(),
        // Removed side's counters (DR-5754): the row's own columns, never the engine header.
        // Absent when the print does not state them. Never null.
        removedTsn: z.number().nonnegative().optional(),
        removedCsn: z.number().nonnegative().optional(),
        // Since-overhaul columns only, never since shop visit.
        removedTso: z.number().nonnegative().optional(),
        removedCso: z.number().nonnegative().optional(),
        // The raw CHG / DISP / CODE cell, verbatim; no enum.
        changeCode: capped().optional(),
        // A software P/N or version printed on this row.
        softwareVersion: capped().optional(),
        remarks: capped().optional(),
        // The print identity, copied onto every row of the page.
        printTitle: capped().optional(),
        shop: capped().optional(),
        workOrder: capped().optional(),
        visitNumber: capped().optional(),
        reportDate: z.iso.date().optional(),
        reportDateRaw: capped().optional(),
        esn: capped().optional(),
        engineModel: capped().optional(),
        // The sub-module group of the row's unit (DR-5776); absent when unknown. Never null.
        moduleGroup: z.enum(MODULE_EXCHANGE_GROUPS).optional(),
        documentId: z.string(),
        pageId: z.string(),
        reconstructedDocumentId: z.string().optional(),
    }))
        .max(ENGINE_CONFIGURATION_ROWS_CAP)
        .superRefine((rows, context) => {
        const seen = new Set();
        rows.forEach((row, index) => {
            const key = JSON.stringify([row.pageId, row.rowIndex]);
            if (seen.has(key)) {
                context.addIssue({
                    code: 'custom',
                    path: [index],
                    message: `duplicate (pageId, rowIndex) ${key}: a printed row ships once per envelope`,
                });
            }
            seen.add(key);
        });
    }),
});
//# sourceMappingURL=engine-configuration-rows-ready.webhook.js.map